import { supabase } from '@/lib/supabase'

/**
 * The public gallery's calls. Everything goes through the gallery edge
 * functions; the viewer never reads a table.
 */

export interface GalleryPhoto {
  id: string
  setId: string
  width: number
  height: number
  lqip: string | null
  thumb: string | null
  display: string | null
}

export interface GalleryFilm {
  id: string
  title: string
  kind: string
  youtube_id: string | null
  drive_url: string | null
}

export interface OpenGallery {
  token: string
  gallery: {
    id: string
    title: string
    subtitle: string | null
    slug: string
    draft: boolean
    coverPhotoId: string | null
    downloadsEnabled: boolean
    needsPin: boolean
    originalsUntil: string | null
  }
  sets: Array<{ id: string; title: string; position: number }>
  photos: GalleryPhoto[]
  films: GalleryFilm[]
}

export type OpenResult =
  | { kind: 'open'; data: OpenGallery }
  | { kind: 'password'; title: string | null; error: string | null }
  | { kind: 'error'; message: string }

async function call<T>(name: string, body: Record<string, unknown>): Promise<{ status: number; data: T }> {
  const { data, error, response } = await supabase.functions.invoke<T>(name, { body })
  if (!error) return { status: 200, data: data as T }
  const res = (error as { context?: Response }).context ?? response
  let parsed: unknown = {}
  try {
    parsed = await res?.json()
  } catch {
    /* not json */
  }
  return { status: res?.status ?? 500, data: parsed as T }
}

const tokenKey = (slug: string) => `gallery-token:${slug}`

export function savedToken(slug: string): string | null {
  try {
    return localStorage.getItem(tokenKey(slug))
  } catch {
    return null
  }
}

export async function openGallery(slug: string, password?: string): Promise<OpenResult> {
  const token = password ? null : savedToken(slug)
  const { status, data } = await call<Partial<OpenGallery> & { needsPassword?: boolean; title?: string; error?: string }>('gallery-open', {
    slug,
    ...(password ? { password } : {}),
    ...(token ? { token } : {}),
  })
  if (status === 200 && data.token && data.gallery) {
    try {
      localStorage.setItem(tokenKey(slug), data.token)
    } catch {
      /* private browsing: ask again next time */
    }
    return { kind: 'open', data: data as OpenGallery }
  }
  if (status === 401 && data.needsPassword) return { kind: 'password', title: data.title ?? null, error: password ? (data.error ?? null) : null }
  return { kind: 'error', message: data.error ?? 'This gallery could not be opened.' }
}

export type DownloadResult =
  | { kind: 'files'; files: Array<{ name: string; url: string }> }
  | { kind: 'pin'; error: string | null }
  | { kind: 'error'; message: string }

export async function downloadLinks(token: string, what: { photoId?: string; setId?: string; all?: boolean }, pin?: string): Promise<DownloadResult> {
  const { status, data } = await call<{ files?: Array<{ name: string; url: string }>; needsPin?: boolean; error?: string }>('gallery-download', {
    token,
    ...what,
    ...(pin ? { pin } : {}),
  })
  if (status === 200 && data.files) return { kind: 'files', files: data.files }
  if (status === 401 && data.needsPin) return { kind: 'pin', error: data.error ?? null }
  return { kind: 'error', message: data.error ?? 'The download could not be prepared.' }
}

export function visitorKey(): string {
  try {
    const existing = localStorage.getItem('gallery-visitor')
    if (existing) return existing
    const fresh = Array.from(crypto.getRandomValues(new Uint8Array(18)), (b) => b.toString(36).padStart(2, '0')).join('').slice(0, 32)
    localStorage.setItem('gallery-visitor', fresh)
    return fresh
  } catch {
    return 'session-' + Math.random().toString(36).slice(2).padEnd(24, '0')
  }
}

export async function favourites(token: string, photoId?: string, on?: boolean): Promise<string[]> {
  const { status, data } = await call<{ favourites?: string[] }>('gallery-favourite', {
    token,
    visitorKey: visitorKey(),
    ...(photoId ? { photoId, on } : {}),
  })
  return status === 200 ? (data.favourites ?? []) : []
}
